import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import MobileWrapper from "../../components/MobileWrapper";
import {
  FiSearch,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiSave,
} from "react-icons/fi";
import { RiWaterPercentFill } from "react-icons/ri";
import { toast } from "react-hot-toast";
import { addNewDoc, db } from "../../utils/firebase/Firebase.utils";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useSelector } from "react-redux";
import { dualNotification } from "../../utils/EleghoUtils";
import { adminEmailAddress } from "../../utils/constants";
import { v4 } from "uuid";

// Generate floors data similar to StackingPlan
const generateFloors = () => {
  const floors = [];

  // Add floors 2-39
  for (let i = 39; i >= 2; i--) {
    let floorUnits = [];

    // Special case for 39th floor
    if (i === 39) {
      floorUnits = [
        { unitNumber: "39A", type: "Residential" },
        { unitNumber: "39B", type: "Residential" },
        { unitNumber: "MOTHER METER", type: "Utility" },
      ];
    }
    // Special case for 38th floor
    else if (i === 38) {
      floorUnits = [
        { unitNumber: "38A", type: "Residential" },
        { unitNumber: "38B", type: "Residential" },
        { unitNumber: "38C", type: "Residential" },
        { unitNumber: "38D", type: "Residential" },
      ];
    }
    // Floors 32-37 (8 units)
    else if (i >= 32 && i <= 37) {
      floorUnits = Array.from({ length: 8 }, (_, j) => ({
        unitNumber: `${i}${String.fromCharCode(65 + j)}`, // 'A' is 65 in ASCII
        type: "Residential",
      }));
    }
    // Floors 9-31 (varying units)
    else if (i >= 9 && i <= 31) {
      const units = [];

      // Special cases for combined units
      if ([15, 19, 21, 22, 23, 24, 25, 26, 27, 29].includes(i)) {
        units.push({ unitNumber: `${i}AB`, type: "Residential" });
        units.push({ unitNumber: `${i}C`, type: "Residential" });
      } else if (i === 16) {
        units.push({ unitNumber: `${i}AB`, type: "Residential" });
        units.push({ unitNumber: `${i}C`, type: "Residential" });
      } else if (i === 20) {
        units.push({ unitNumber: `${i}A`, type: "Residential" });
        units.push({ unitNumber: `${i}B`, type: "Residential" });
      } else if (i === 30) {
        units.push({ unitNumber: `${i}ABC`, type: "Residential" });
        units.push({ unitNumber: `${i}D`, type: "Residential" });
      } else {
        units.push({ unitNumber: `${i}A`, type: "Residential" });
        units.push({ unitNumber: `${i}B`, type: "Residential" });
      }

      // Add remaining units
      const unitLetters = [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
      ];
      for (let j = 2; j < unitLetters.length; j++) {
        if ([25, 26, 27, 28, 30].includes(i) && j === unitLetters.length - 1) {
          units.push({
            unitNumber: `${i}${unitLetters[j]}L`,
            type: "Residential",
          });
        } else {
          units.push({
            unitNumber: `${i}${unitLetters[j]}`,
            type: "Residential",
          });
        }
      }

      floorUnits = units;
    }
    // Floors 2-8 (15-17 units)
    else if (i >= 2 && i <= 8) {
      const unitLetters = [
        "A",
        "B",
        "C",
        "D",
        "E",
        "F",
        "G",
        "H",
        "I",
        "J",
        "K",
        "L",
        "M",
        "N",
        "O",
        "P",
        "Q",
      ];
      floorUnits = unitLetters.map((letter) => ({
        unitNumber: `${i}${letter}`,
        type: "Residential",
      }));
    }

    floors.push({
      floorNumber: i,
      units: floorUnits,
    });
  }

  return floors;
};

const FLOORS_DATA = generateFloors();

function WaterMeterReadingTableView() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);
  const [readings, setReadings] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFloors, setExpandedFloors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meterId, setMeterId] = useState(v4().slice(0, 8).toUpperCase());

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  // Set current date and time as default
  useEffect(() => {
    const now = new Date();
    const localDateTime = new Date(now - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setReadings((prev) => ({
      ...prev,
      dateTime: localDateTime,
    }));
  }, []);

  const toggleFloor = (floorNumber) => {
    setExpandedFloors((prev) => ({
      ...prev,
      [floorNumber]: !prev[floorNumber],
    }));
  };

  const handleInputChange = (unitNumber, value) => {
    setReadings((prev) => ({
      ...prev,
      [unitNumber]: value,
    }));
  };

  const handleSubmit = async () => {
    // Validate if at least one reading is entered
    const hasReadings =
      Object.entries(readings).filter(
        ([key, value]) => key !== "dateTime" && value?.trim()
      ).length > 0;

    if (!hasReadings) {
      toast.error("Please enter at least one meter reading");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current date and time
      const now = new Date();
      const dateTime = readings.dateTime || now.toISOString();

      // Prepare readings data for submission
      const readingsToSubmit = Object.entries(readings)
        .filter(([key, value]) => key !== "dateTime" && value?.trim())
        .map(([unitNumber, meterReading]) => ({
          unitNumber,
          meterReading,
          dateTime,
          status: "saved",
          createdAt: now.toISOString(),
          createdBy: currentUser?.email || "Unknown",
          createdByName: currentUser?.displayName || "Unknown",
          batchId: meterId, // To identify readings from the same batch
          // Note: No imageUrl as this is batch entry mode
        }));

      // Save all readings and collect their document references
      const savedDocRefs = await Promise.all(
        readingsToSubmit.map((reading) => addNewDoc("meterReading", reading))
      );

      // Send notifications for each unit
      await Promise.all(
        readingsToSubmit.map(async (reading, index) => {
          const docRef = savedDocRefs[index];
          if (!docRef) return;

          try {
            // Find users with matching linkedIdentifiers for the unit number
            const usersRef = collection(db, "users");
            const q = query(
              usersRef,
              where("linkedIdentifiers", "array-contains", reading.unitNumber)
            );
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
              // Collect user emails for notification
              const users = [];
              querySnapshot.forEach((doc) => {
                const userData = doc.data();
                if (userData) {
                  users.push(userData);
                }
              });

              // Send notifications to all users associated with the unit
              if (users.length > 0) {
                await Promise.all(
                  users.map(async (user) => {
                    const notification = {
                      recipient: user.email,
                      recipientName: user.displayName,
                      sender: currentUser?.email,
                      senderName: currentUser?.displayName,
                      subject: "Water Meter Reading",
                      message: `A new water meter reading has been logged for your unit ${reading.unitNumber}.`,
                      type: "water-meter-reading",
                      id: docRef.id,
                      read: false,
                    };

                    await dualNotification(notification);
                  })
                );
              }
            }
          } catch (error) {
            console.error("Error sending notifications:", error);
          }
        })
      );

      // Send admin notification
      const adminNotification = {
        recipient: adminEmailAddress,
        recipientName: "Admin",
        sender: currentUser?.email,
        senderName: currentUser?.displayName,
        subject: "Water Meter Reading",
        message: `${readingsToSubmit.length} new water meter readings have been logged (Batch: ${meterId}).`,
        type: "water-meter-reading",
        url: `/property-management?type=Water+Meter+Reading`,
        id: meterId,
        read: false,
      };

      await dualNotification(adminNotification);

      toast.success(
        `${readingsToSubmit.length} water meter readings saved successfully`
      );
      setReadings({ dateTime });
      navigate("/water-logs");
    } catch (error) {
      console.error("Error submitting readings:", error);
      toast.error("Failed to save meter readings");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter floors and units based on search term
  const filteredFloors = FLOORS_DATA.map((floor) => {
    const filteredUnits = floor.units.filter((unit) =>
      unit.unitNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return {
      ...floor,
      units: filteredUnits,
      filtered: filteredUnits.length > 0,
    };
  }).filter((floor) => floor.filtered);

  return (
    <MobileWrapper label="Water Meter Reading">
      <div className="min-h-screen bg-gray-50 p-4 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5 bg-[#363062]/5 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <RiWaterPercentFill className="text-white text-sm" />
            </div>
            <div>
              <h1 className="text-xs font-medium text-[#363062]">
                Water Meter Reading - Batch Mode
              </h1>
              <p className="text-xs text-gray-500">
                Record multiple meter readings at once
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Batch ID: {meterId}
              </p>
            </div>
          </div>
          <Link
            to="/water-logs"
            className="text-xs text-eleghoBlue flex items-center"
          >
            <span>View Logs</span>
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="text-sm mb-4">
            Input the water meter reading per unit number below
          </div>

          {/* Date and Time */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Date and Time
            </label>
            <input
              type="datetime-local"
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-sm"
              value={readings.dateTime || ""}
              onChange={(e) =>
                setReadings((prev) => ({ ...prev, dateTime: e.target.value }))
              }
              disabled={true}
            />
          </div>

          {/* Search bar */}
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search unit number..."
              className="pl-10 pr-3 py-2 w-full border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Floor and Unit List */}
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredFloors.length > 0 ? (
              filteredFloors.map((floor) => (
                <div
                  key={floor.floorNumber}
                  className="mb-3 border border-gray-100 rounded-md"
                >
                  <div
                    className="flex justify-between items-center p-3 bg-gray-50 cursor-pointer"
                    onClick={() => toggleFloor(floor.floorNumber)}
                  >
                    <h3 className="text-sm font-medium">
                      Floor {floor.floorNumber}
                    </h3>
                    {expandedFloors[floor.floorNumber] ? (
                      <FiChevronUp className="text-gray-500" />
                    ) : (
                      <FiChevronDown className="text-gray-500" />
                    )}
                  </div>

                  {expandedFloors[floor.floorNumber] && (
                    <div className="p-3">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">
                              Unit
                            </th>
                            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">
                              Reading
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {floor.units.map((unit) => (
                            <tr
                              key={unit.unitNumber}
                              className="border-t border-gray-100"
                            >
                              <td className="py-2 text-sm">
                                {unit.unitNumber}
                              </td>
                              <td className="py-2">
                                <input
                                  type="text"
                                  className="w-full px-2 py-1 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-600 focus:border-blue-600 text-sm"
                                  placeholder="Enter reading"
                                  value={readings[unit.unitNumber] || ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      unit.unitNumber,
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                No units found matching your search
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full py-3 bg-[#363062] text-white rounded-lg flex items-center justify-center space-x-2 disabled:bg-gray-400"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Saving Readings...</span>
            </>
          ) : (
            <>
              <FiSave className="w-4 h-4" />
              <span>Save All Readings</span>
            </>
          )}
        </button>

        <p className="text-[10px] text-center text-gray-500 mt-3">
          Note: Batch mode does not include meter photos. For photo
          documentation, please use the single entry mode.
        </p>
      </div>
    </MobileWrapper>
  );
}

export default WaterMeterReadingTableView;
